import { Divider, Stack, Switch, Typography } from '@mui/material'
import { useConfiguration, useSaveConfiguration } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'

/**
 * One switch on the printed bill: what it turns off, and what it says when on.
 *
 * <p>The label reads as the line on the paper, not as a field name. Somebody
 * setting this up is looking at a bill, not at a schema.</p>
 */
type Field = {
  key: string
  label: string
  help: string
  /** What the bill does when nobody has said anything. */
  standard: boolean
}

const HEAD: Field[] = [
  {
    key: 'invoice.print.company.name',
    label: 'The trading name beside the mark',
    help: 'The logo usually carries the name already. A branch that has uploaded no mark is named here whatever this says — a bill that names nobody at the top is not an option.',
    standard: false,
  },
  {
    key: 'invoice.print.service.heading',
    label: 'What was sold, under the title',
    help: 'Banquet, Outdoor catering, Bulk takeaway — named the way you name it in Services.',
    standard: true,
  },
  {
    key: 'invoice.print.due.date',
    label: 'The due date, in the title band',
    help: 'Off by default: a date in the band reads as a demand on the proforma, which is not one.',
    standard: false,
  },
]

const PARTIES: Field[] = [
  {
    key: 'invoice.print.place.of.supply',
    label: 'Place of supply',
    help: 'The state and its GST number — Tamil Nadu (33). Expected on a tax invoice.',
    standard: true,
  },
  {
    key: 'invoice.print.fulfilment',
    label: 'Fulfilment',
    help: 'Where the food was served: at our hall, at the site, over the counter.',
    standard: true,
  },
  {
    key: 'invoice.print.note',
    label: 'The note on the invoice',
    help: 'Printed with the customer it is about, so an instruction about the job or the payment is read before the figures.',
    standard: true,
  },
  {
    key: 'invoice.print.order.number',
    label: 'Our order number',
    help: 'Ours to track the job by. On, so a customer ringing up can quote it back; off, the bill carries only its own number.',
    standard: false,
  },
  {
    key: 'invoice.print.status',
    label: 'The invoice status',
    help: 'A workflow state of ours. A draft says so in its title and a cancelled bill in its footnote, whatever this says.',
    standard: false,
  },
]

const FOOT: Field[] = [
  {
    key: 'invoice.print.amount.in.words',
    label: 'The amount in words',
    help: 'Rupees One Lakh Eighty Nine Thousand only. The line that makes a bill hard to alter.',
    standard: true,
  },
  {
    key: 'invoice.print.payment',
    label: 'Where to send the money',
    help: "The branch's account, under the total. Nothing prints where the branch has given no account.",
    standard: true,
  },
  {
    key: 'invoice.print.tax.summary',
    label: 'The tax summary',
    help: 'What the tax was made of, heading by heading and rate by rate. An accounts team claims input credit off this.',
    standard: true,
  },
  {
    key: 'invoice.print.signature',
    label: 'The signature strip',
    help: "The customer's acknowledgement beside yours, for a copy that goes back with the crew.",
    standard: true,
  },
]

/**
 * What the printed bill carries.
 *
 * <p>Each of these was settled in code and argued over a field at a time. What
 * goes on a bill is a house style: one business wants its order number on the
 * customer's copy to quote back over the phone, and the next wants nothing on
 * it that is not the customer's business.</p>
 *
 * <p>Nothing here changes what is charged, filed or sent to the portal - only
 * what the paper says. The tax invoice and the proforma are set together,
 * because they are one template.</p>
 */
export function InvoicePrintTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const writable = canWrite('config')

  const { data: settings, error: settingsError } = useConfiguration()
  const saveSetting = useSaveConfiguration()

  /** What is set, or what the bill does when nothing is. */
  const on = (field: Field) => {
    const held = (settings ?? []).find((one) => one.key === field.key)
    return held ? held.value === 'true' : field.standard
  }

  const set = (field: Field, checked: boolean) =>
    saveSetting.mutate({ key: field.key, value: String(checked), valueType: 'BOOLEAN' })

  const group = (heading: string, caption: string, fields: Field[]) => (
    <>
      <Typography variant="subtitle2">{heading}</Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
      <Stack spacing={1.25} sx={{ my: 2 }}>
        {fields.map((field) => (
          <Stack key={field.key} direction="row" spacing={1.5} alignItems="flex-start">
            <Switch
              size="small"
              checked={on(field)}
              disabled={!writable || saveSetting.isPending}
              onChange={(event) => set(field, event.target.checked)}
              inputProps={{ 'aria-label': field.label }}
            />
            <div>
              <Typography variant="body2">{field.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {field.help}
              </Typography>
            </div>
          </Stack>
        ))}
      </Stack>
    </>
  )

  return (
    <>
      <Typography variant="subtitle2">What the printed bill carries</Typography>
      <Typography variant="caption" color="text.secondary">
        The tax invoice and the proforma are one template and are set together. Nothing here changes
        what is charged, what is filed or what goes to the e-invoice portal — only what the paper
        says. Leave it alone and the bill is the one you have now.
      </Typography>

      <ErrorBanner error={settingsError ?? saveSetting.error} />

      <Divider sx={{ my: 2 }} />
      {group(
        'The head of the page',
        'The mark, the address and the licences always print. These are the rest of it.',
        HEAD,
      )}

      <Divider sx={{ my: 2 }} />
      {group(
        'Who it is for, and what it is for',
        'The two boxes under the title band. The billing name, address and GSTIN always print.',
        PARTIES,
      )}

      <Divider sx={{ my: 2 }} />
      {group(
        'The foot of the page',
        'Below the particulars. The totals and the terms are set elsewhere — the terms by the boxes ticked against each clause, under Terms.',
        FOOT,
      )}
    </>
  )
}
